import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../ui/dialog';
import { 
  Calendar,
  Clock,
  Settings,
  Plus,
  Trash2,
  Save,
  Loader2,
  MapPin,
  RefreshCw,
  CheckCircle2,
  XCircle,
  CalendarX,
  Link2
} from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const DAYS_OF_WEEK = [
  { id: 'monday', label: 'Monday', short: 'Mon' },
  { id: 'tuesday', label: 'Tuesday', short: 'Tue' },
  { id: 'wednesday', label: 'Wednesday', short: 'Wed' },
  { id: 'thursday', label: 'Thursday', short: 'Thu' },
  { id: 'friday', label: 'Friday', short: 'Fri' },
  { id: 'saturday', label: 'Saturday', short: 'Sat' },
  { id: 'sunday', label: 'Sunday', short: 'Sun' },
];

const TIME_OPTIONS = Array.from({ length: 48 }, (_, i) => {
  const hours = Math.floor(i / 2);
  const minutes = i % 2 === 0 ? '00' : '30';
  return `${hours.toString().padStart(2, '0')}:${minutes}`;
});

/**
 * ProviderCalendarSetup - Provider portal component for managing availability
 */
const ProviderCalendarSetup = ({ providerId, category = 'default' }) => {
  const [activeTab, setActiveTab] = useState('schedule');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  // Schedule state
  const [schedule, setSchedule] = useState(null);
  const [blockedDates, setBlockedDates] = useState([]);
  const [serviceDurations, setServiceDurations] = useState([]);
  const [mobileConfig, setMobileConfig] = useState(null);
  const [syncConfig, setSyncConfig] = useState(null);
  
  // Modal states
  const [showBlockDateModal, setShowBlockDateModal] = useState(false);
  const [newBlockedDate, setNewBlockedDate] = useState({ date: '', reason: '', all_day: true });
  const [showServiceDurationModal, setShowServiceDurationModal] = useState(false);
  const [newServiceDuration, setNewServiceDuration] = useState({ 
    service_name: '', 
    duration_minutes: 60, 
    price: '', 
    requires_deposit: false,
    deposit_amount: ''
  });

  useEffect(() => {
    if (providerId) {
      fetchAllData();
    }
  }, [providerId]);

  const fetchAllData = async () => {
    try {
      setLoading(true);
      const [scheduleRes, blockedRes, durationsRes, mobileRes, syncRes] = await Promise.all([
        axios.get(`${API_URL}/api/availability/schedule/${providerId}`),
        axios.get(`${API_URL}/api/availability/blocked-dates/${providerId}`),
        axios.get(`${API_URL}/api/availability/service-durations/${providerId}`),
        axios.get(`${API_URL}/api/availability/mobile-config/${providerId}`),
        axios.get(`${API_URL}/api/availability/sync-config/${providerId}`),
      ]);
      
      setSchedule(scheduleRes.data);
      setBlockedDates(blockedRes.data.blocked_dates || []);
      setServiceDurations(durationsRes.data.service_durations || []);
      setMobileConfig(mobileRes.data);
      setSyncConfig(syncRes.data);
    } catch (error) {
      console.error('Failed to fetch calendar data:', error);
    } finally {
      setLoading(false);
    }
  };

  const saveSchedule = async () => {
    try {
      setSaving(true);
      await axios.post(`${API_URL}/api/availability/schedule`, {
        provider_id: providerId,
        ...schedule
      });
      alert('Schedule saved successfully!');
    } catch (error) {
      console.error('Failed to save schedule:', error);
      alert('Failed to save schedule. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const toggleDayAvailability = (dayId) => {
    setSchedule(prev => ({
      ...prev,
      schedule: prev.schedule.map(day => 
        day.day === dayId ? { ...day, is_available: !day.is_available } : day
      )
    }));
  };

  const updateDayTime = (dayId, field, value) => {
    setSchedule(prev => ({
      ...prev,
      schedule: prev.schedule.map(day => {
        if (day.day === dayId) {
          const updatedSlots = [...(day.slots || [])];
          if (updatedSlots.length === 0) {
            updatedSlots.push({ start_time: '09:00', end_time: '17:00' });
          }
          if (field === 'start_time') {
            updatedSlots[0] = { ...updatedSlots[0], start_time: value };
          } else if (field === 'end_time') {
            updatedSlots[0] = { ...updatedSlots[0], end_time: value };
          }
          return { ...day, slots: updatedSlots };
        }
        return day;
      })
    }));
  };

  const updateBreakTime = (dayId, field, value) => {
    setSchedule(prev => ({
      ...prev,
      schedule: prev.schedule.map(day => 
        day.day === dayId ? { ...day, [field]: value } : day
      )
    }));
  };

  const addBlockedDate = async () => {
    try {
      const response = await axios.post(`${API_URL}/api/availability/blocked-dates`, {
        provider_id: providerId,
        ...newBlockedDate
      });
      setBlockedDates([...blockedDates, response.data.blocked_date]);
      setShowBlockDateModal(false);
      setNewBlockedDate({ date: '', reason: '', all_day: true });
    } catch (error) {
      console.error('Failed to add blocked date:', error);
      alert('Failed to add blocked date');
    }
  };

  const removeBlockedDate = async (blockedId) => {
    try {
      await axios.delete(`${API_URL}/api/availability/blocked-dates/${blockedId}`);
      setBlockedDates(blockedDates.filter(b => b.id !== blockedId));
    } catch (error) {
      console.error('Failed to remove blocked date:', error);
    }
  };

  const addServiceDuration = async () => {
    try {
      await axios.post(`${API_URL}/api/availability/service-durations`, {
        provider_id: providerId,
        service_id: newServiceDuration.service_name.toLowerCase().replace(/\s+/g, '-'),
        ...newServiceDuration,
        price: newServiceDuration.price ? parseFloat(newServiceDuration.price) : null,
        deposit_amount: newServiceDuration.deposit_amount ? parseFloat(newServiceDuration.deposit_amount) : null
      });
      await fetchAllData();
      setShowServiceDurationModal(false);
      setNewServiceDuration({ service_name: '', duration_minutes: 60, price: '', requires_deposit: false, deposit_amount: '' });
    } catch (error) {
      console.error('Failed to add service duration:', error);
    }
  };

  const saveMobileConfig = async () => {
    try {
      setSaving(true);
      await axios.post(`${API_URL}/api/availability/mobile-config`, {
        provider_id: providerId,
        ...mobileConfig
      });
      alert('Mobile visit settings saved!');
    } catch (error) {
      console.error('Failed to save mobile config:', error);
    } finally {
      setSaving(false);
    }
  };

  const saveSyncConfig = async () => {
    try {
      setSaving(true);
      await axios.post(`${API_URL}/api/availability/sync-config`, {
        provider_id: providerId,
        ...syncConfig
      });
      alert('Calendar sync settings saved!');
    } catch (error) {
      console.error('Failed to save sync config:', error);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-purple-600" />
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4" data-testid="provider-calendar-setup">
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid grid-cols-5 w-full max-w-2xl">
          <TabsTrigger value="schedule">
            <Clock className="h-4 w-4 mr-1" />
            Schedule
          </TabsTrigger>
          <TabsTrigger value="blocked">
            <CalendarX className="h-4 w-4 mr-1" />
            Blocked
          </TabsTrigger>
          <TabsTrigger value="services">
            <Settings className="h-4 w-4 mr-1" />
            Services
          </TabsTrigger>
          <TabsTrigger value="mobile">
            <MapPin className="h-4 w-4 mr-1" />
            Mobile
          </TabsTrigger>
          <TabsTrigger value="sync">
            <Link2 className="h-4 w-4 mr-1" />
            Sync
          </TabsTrigger>
        </TabsList>

        {/* Weekly Schedule Tab */}
        <TabsContent value="schedule">
          <Card>
            <CardHeader>
              <CardTitle>Weekly Availability</CardTitle>
              <CardDescription>Set your regular working hours for each day</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* General Settings */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 p-4 bg-gray-50 rounded-lg">
                <div>
                  <Label>Default Slot Duration</Label>
                  <Select 
                    value={String(schedule?.slot_duration_minutes || 60)}
                    onValueChange={(v) => setSchedule({ ...schedule, slot_duration_minutes: parseInt(v) })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="30">30 minutes</SelectItem>
                      <SelectItem value="45">45 minutes</SelectItem>
                      <SelectItem value="60">1 hour</SelectItem>
                      <SelectItem value="90">1.5 hours</SelectItem>
                      <SelectItem value="120">2 hours</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Buffer Time</Label>
                  <Select 
                    value={String(schedule?.buffer_minutes || 15)}
                    onValueChange={(v) => setSchedule({ ...schedule, buffer_minutes: parseInt(v) })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="0">No buffer</SelectItem>
                      <SelectItem value="10">10 minutes</SelectItem>
                      <SelectItem value="15">15 minutes</SelectItem>
                      <SelectItem value="30">30 minutes</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Advance Booking</Label>
                  <Select 
                    value={String(schedule?.advance_booking_days || 30)}
                    onValueChange={(v) => setSchedule({ ...schedule, advance_booking_days: parseInt(v) })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="7">1 week</SelectItem>
                      <SelectItem value="14">2 weeks</SelectItem>
                      <SelectItem value="30">1 month</SelectItem>
                      <SelectItem value="60">2 months</SelectItem>
                      <SelectItem value="90">3 months</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex flex-col gap-2">
                  <Label>Options</Label>
                  <div className="flex items-center gap-2">
                    <Switch
                      checked={schedule?.same_day_booking ?? true}
                      onCheckedChange={(v) => setSchedule({ ...schedule, same_day_booking: v })}
                    />
                    <span className="text-sm">Same-day booking</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Switch
                      checked={schedule?.requires_approval ?? false}
                      onCheckedChange={(v) => setSchedule({ ...schedule, requires_approval: v })}
                    />
                    <span className="text-sm">Require approval</span>
                  </div>
                </div>
              </div>

              {/* Day-by-day Schedule */}
              <div className="space-y-3">
                {DAYS_OF_WEEK.map((day) => {
                  const daySchedule = schedule?.schedule?.find(s => s.day === day.id) || {};
                  const isAvailable = daySchedule.is_available ?? true;
                  const slot = daySchedule.slots?.[0] || { start_time: '09:00', end_time: '17:00' };

                  return (
                    <div 
                      key={day.id}
                      className={`p-4 rounded-lg border transition-all ${
                        isAvailable ? 'bg-white' : 'bg-gray-50 opacity-60'
                      }`}
                    >
                      <div className="flex items-center gap-4">
                        <div className="w-24">
                          <div className="flex items-center gap-2">
                            <Switch
                              checked={isAvailable}
                              onCheckedChange={() => toggleDayAvailability(day.id)}
                            />
                            <span className="font-medium">{day.short}</span>
                          </div>
                        </div>

                        {isAvailable && (
                          <>
                            <div className="flex items-center gap-2">
                              <Select
                                value={slot.start_time}
                                onValueChange={(v) => updateDayTime(day.id, 'start_time', v)}
                              >
                                <SelectTrigger className="w-24">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  {TIME_OPTIONS.map(t => (
                                    <SelectItem key={t} value={t}>{t}</SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                              <span className="text-gray-500">to</span>
                              <Select
                                value={slot.end_time}
                                onValueChange={(v) => updateDayTime(day.id, 'end_time', v)}
                              >
                                <SelectTrigger className="w-24">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  {TIME_OPTIONS.map(t => (
                                    <SelectItem key={t} value={t}>{t}</SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>

                            <div className="flex items-center gap-2 ml-4">
                              <span className="text-sm text-gray-500">Break:</span>
                              <Select
                                value={daySchedule.break_start || 'none'}
                                onValueChange={(v) => updateBreakTime(day.id, 'break_start', v === 'none' ? null : v)}
                              >
                                <SelectTrigger className="w-24">
                                  <SelectValue placeholder="None" />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="none">None</SelectItem>
                                  {TIME_OPTIONS.map(t => (
                                    <SelectItem key={t} value={t}>{t}</SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                              {daySchedule.break_start && (
                                <>
                                  <span className="text-gray-500">-</span>
                                  <Select
                                    value={daySchedule.break_end || '13:00'}
                                    onValueChange={(v) => updateBreakTime(day.id, 'break_end', v)}
                                  >
                                    <SelectTrigger className="w-24">
                                      <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                      {TIME_OPTIONS.map(t => (
                                        <SelectItem key={t} value={t}>{t}</SelectItem>
                                      ))}
                                    </SelectContent>
                                  </Select>
                                </>
                              )}
                            </div>
                          </>
                        )}

                        {!isAvailable && (
                          <span className="text-gray-500">Closed</span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>

              <Button onClick={saveSchedule} disabled={saving} className="w-full">
                {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
                Save Schedule
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Blocked Dates Tab */}
        <TabsContent value="blocked">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Blocked Dates</CardTitle>
                  <CardDescription>Block specific dates when you&apos;re unavailable</CardDescription>
                </div>
                <Button onClick={() => setShowBlockDateModal(true)}>
                  <Plus className="h-4 w-4 mr-2" />
                  Block Date
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {blockedDates.length === 0 ? (
                <p className="text-center text-gray-500 py-8">No blocked dates</p>
              ) : (
                <div className="space-y-2">
                  {blockedDates.map((blocked) => (
                    <div key={blocked.id} className="flex items-center justify-between p-3 bg-red-50 rounded-lg">
                      <div className="flex items-center gap-3">
                        <CalendarX className="h-5 w-5 text-red-500" />
                        <div>
                          <p className="font-medium">{blocked.date}</p>
                          {blocked.reason && (
                            <p className="text-sm text-gray-600">{blocked.reason}</p>
                          )}
                        </div>
                        {blocked.all_day && (
                          <Badge variant="secondary">All day</Badge>
                        )}
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => removeBlockedDate(blocked.id)}
                      >
                        <Trash2 className="h-4 w-4 text-red-500" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Service Durations Tab */}
        <TabsContent value="services">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Service Durations</CardTitle>
                  <CardDescription>Set custom durations for each service type</CardDescription>
                </div>
                <Button onClick={() => setShowServiceDurationModal(true)}>
                  <Plus className="h-4 w-4 mr-2" />
                  Add Service
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {serviceDurations.length === 0 ? (
                <p className="text-center text-gray-500 py-8">
                  No custom service durations. Using default: {schedule?.slot_duration_minutes || 60} minutes
                </p>
              ) : (
                <div className="space-y-2">
                  {serviceDurations.map((service) => (
                    <div key={service.id || service.service_id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <div>
                        <p className="font-medium">{service.service_name}</p>
                        <div className="flex items-center gap-2 text-sm text-gray-600">
                          <Clock className="h-3 w-3" />
                          {service.duration_minutes} minutes
                          {service.price && (
                            <>
                              <span className="mx-1">•</span>
                              Rs. {service.price}
                            </>
                          )}
                          {service.requires_deposit && (
                            <Badge variant="outline" className="text-xs">
                              Deposit: Rs. {service.deposit_amount}
                            </Badge>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Mobile Visit Tab */}
        <TabsContent value="mobile">
          <Card>
            <CardHeader>
              <CardTitle>Mobile Visit Settings</CardTitle>
              <CardDescription>Configure if you offer services at customer locations</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center gap-4">
                <Switch
                  checked={mobileConfig?.offers_mobile_visits ?? false}
                  onCheckedChange={(v) => setMobileConfig({ ...mobileConfig, offers_mobile_visits: v })}
                />
                <Label>I offer mobile visits (I go to the customer)</Label>
              </div>

              {mobileConfig?.offers_mobile_visits && (
                <>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Travel Radius (km)</Label>
                      <Input
                        type="number"
                        value={mobileConfig?.travel_radius_km || 10}
                        onChange={(e) => setMobileConfig({ 
                          ...mobileConfig, 
                          travel_radius_km: parseFloat(e.target.value) 
                        })}
                      />
                    </div>
                    <div>
                      <Label>Mobile Visit Fee (Rs.)</Label>
                      <Input
                        type="number"
                        value={mobileConfig?.mobile_visit_fee || 0}
                        onChange={(e) => setMobileConfig({ 
                          ...mobileConfig, 
                          mobile_visit_fee: parseFloat(e.target.value) 
                        })}
                      />
                    </div>
                  </div>

                  <div>
                    <Label>Minimum Booking Notice (hours)</Label>
                    <Select
                      value={String(mobileConfig?.min_booking_notice_hours || 24)}
                      onValueChange={(v) => setMobileConfig({ 
                        ...mobileConfig, 
                        min_booking_notice_hours: parseInt(v) 
                      })}
                    >
                      <SelectTrigger className="w-48">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="2">2 hours</SelectItem>
                        <SelectItem value="4">4 hours</SelectItem>
                        <SelectItem value="12">12 hours</SelectItem>
                        <SelectItem value="24">24 hours</SelectItem>
                        <SelectItem value="48">48 hours</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label>Areas Served (comma-separated)</Label>
                    <Input
                      placeholder="e.g., Colombo 3, Colombo 4, Nugegoda"
                      value={mobileConfig?.areas_served?.join(', ') || ''}
                      onChange={(e) => setMobileConfig({ 
                        ...mobileConfig, 
                        areas_served: e.target.value.split(',').map(s => s.trim()).filter(Boolean)
                      })}
                    />
                  </div>
                </>
              )}

              <Button onClick={saveMobileConfig} disabled={saving}>
                {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
                Save Mobile Settings
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Calendar Sync Tab */}
        <TabsContent value="sync">
          <Card>
            <CardHeader>
              <CardTitle>Calendar Sync</CardTitle>
              <CardDescription>Sync with external calendars to avoid double-booking</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-4">
                <div className="flex items-center gap-4 p-4 border rounded-lg">
                  <input
                    type="radio"
                    id="internal"
                    name="syncType"
                    checked={syncConfig?.sync_type === 'internal'}
                    onChange={() => setSyncConfig({ ...syncConfig, sync_type: 'internal' })}
                  />
                  <label htmlFor="internal" className="flex-1 cursor-pointer">
                    <p className="font-medium">Internal Calendar</p>
                    <p className="text-sm text-gray-500">Use only the Zeilian calendar system</p>
                  </label>
                </div>

                <div className="flex items-center gap-4 p-4 border rounded-lg">
                  <input
                    type="radio"
                    id="google"
                    name="syncType"
                    checked={syncConfig?.sync_type === 'google'}
                    onChange={() => setSyncConfig({ ...syncConfig, sync_type: 'google' })}
                  />
                  <label htmlFor="google" className="flex-1 cursor-pointer">
                    <p className="font-medium flex items-center gap-2">
                      Google Calendar
                      {syncConfig?.google_connected && (
                        <Badge className="bg-green-100 text-green-700">Connected</Badge>
                      )}
                    </p>
                    <p className="text-sm text-gray-500">Sync your Google Calendar to block busy times</p>
                  </label>
                </div>
              </div>

              {syncConfig?.sync_type === 'google' && !syncConfig?.google_connected && (
                <Button variant="outline" className="w-full">
                  <img src="https://www.google.com/favicon.ico" alt="" className="h-4 w-4 mr-2" />
                  Connect Google Calendar
                </Button>
              )}

              {syncConfig?.sync_type === 'google' && syncConfig?.google_connected && (
                <div className="p-4 bg-green-50 rounded-lg">
                  <p className="text-green-700 flex items-center gap-2">
                    <CheckCircle2 className="h-4 w-4" />
                    Google Calendar connected
                  </p>
                  {syncConfig?.last_synced && (
                    <p className="text-sm text-green-600 mt-1">
                      Last synced: {new Date(syncConfig.last_synced).toLocaleString()}
                    </p>
                  )}
                  <div className="flex gap-2 mt-3">
                    <Button variant="outline" size="sm">
                      <RefreshCw className="h-4 w-4 mr-1" />
                      Sync Now
                    </Button>
                    <Button variant="outline" size="sm" className="text-red-600">
                      <XCircle className="h-4 w-4 mr-1" />
                      Disconnect
                    </Button>
                  </div>
                </div>
              )}

              <Button onClick={saveSyncConfig} disabled={saving}>
                {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
                Save Sync Settings
              </Button>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Block Date Modal */}
      <Dialog open={showBlockDateModal} onOpenChange={setShowBlockDateModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Block a Date</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Date</Label>
              <Input
                type="date"
                value={newBlockedDate.date}
                onChange={(e) => setNewBlockedDate({ ...newBlockedDate, date: e.target.value })}
                min={new Date().toISOString().split('T')[0]}
              />
            </div>
            <div>
              <Label>Reason (optional)</Label>
              <Input
                placeholder="e.g., Holiday, Personal day"
                value={newBlockedDate.reason}
                onChange={(e) => setNewBlockedDate({ ...newBlockedDate, reason: e.target.value })}
              />
            </div>
            <div className="flex items-center gap-2">
              <Switch
                checked={newBlockedDate.all_day}
                onCheckedChange={(v) => setNewBlockedDate({ ...newBlockedDate, all_day: v })}
              />
              <Label>Block entire day</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowBlockDateModal(false)}>
              Cancel
            </Button>
            <Button onClick={addBlockedDate} disabled={!newBlockedDate.date}>
              Block Date
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Service Duration Modal */}
      <Dialog open={showServiceDurationModal} onOpenChange={setShowServiceDurationModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Service Duration</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Service Name</Label>
              <Input
                placeholder="e.g., Basic Facial, Bridal Makeup"
                value={newServiceDuration.service_name}
                onChange={(e) => setNewServiceDuration({ ...newServiceDuration, service_name: e.target.value })}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Duration (minutes)</Label>
                <Select
                  value={String(newServiceDuration.duration_minutes)}
                  onValueChange={(v) => setNewServiceDuration({ ...newServiceDuration, duration_minutes: parseInt(v) })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="30">30 minutes</SelectItem>
                    <SelectItem value="45">45 minutes</SelectItem>
                    <SelectItem value="60">1 hour</SelectItem>
                    <SelectItem value="90">1.5 hours</SelectItem>
                    <SelectItem value="120">2 hours</SelectItem>
                    <SelectItem value="180">3 hours</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Price (Rs.)</Label>
                <Input
                  type="number"
                  placeholder="Optional"
                  value={newServiceDuration.price}
                  onChange={(e) => setNewServiceDuration({ ...newServiceDuration, price: e.target.value })}
                />
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Switch
                checked={newServiceDuration.requires_deposit}
                onCheckedChange={(v) => setNewServiceDuration({ ...newServiceDuration, requires_deposit: v })}
              />
              <Label>Requires deposit</Label>
            </div>
            {newServiceDuration.requires_deposit && (
              <div>
                <Label>Deposit Amount (Rs.)</Label>
                <Input
                  type="number"
                  value={newServiceDuration.deposit_amount}
                  onChange={(e) => setNewServiceDuration({ ...newServiceDuration, deposit_amount: e.target.value })}
                />
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowServiceDurationModal(false)}>
              Cancel
            </Button>
            <Button onClick={addServiceDuration} disabled={!newServiceDuration.service_name}>
              Add Service
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ProviderCalendarSetup;
